import React from 'react';
import Typography from '../../../common/StyledTypography';
import Paper from 'apollo-react/components/Paper';
import Doctor2 from 'apollo-react-icons/Doctor2';
import ChartPie from 'apollo-react-icons/ChartPie';
import Table from 'apollo-react-icons/Table';
import Route from 'apollo-react-icons/Route';
import ArrowRight from 'apollo-react-icons/ArrowRight';
import Trash from 'apollo-react-icons/Trash';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { convertUTCToLocal } from '../../../utils/dateUtils';
// Use the same styles as other request cards
import '../../../common/Reviews_Cards/styles.css';
import './DraftCard.css';

/**
 * DraftCard - Card component for displaying a draft request
 * Matches the exact structure of TrialCard for consistency
 *
 * @param {Object} draft - Draft data object
 * @param {Function} onDelete - Callback when delete button is clicked
 */
const DraftCard = ({ draft, onDelete }) => {
  const { t } = useTranslation();

  const studyName = draft?.study_name || draft?.request?.study_name || 'N/A';
  // For drafts, show "N/A" if no request ID (new draft), otherwise show the generated_id
  const generatedId = draft?.request?.generated_id || t('not_available');
  const stageName = draft?.stage?.name || 'N/A';

  // Build navigation URL for viewing the draft
  // For new drafts, navigate to the new request page with draft_id param
  // For send-back drafts, navigate to the request progress page
  const requestId = draft?.request?.id;
  const navigateTo = requestId
    ? `/customer/progress/requests/${requestId}?draft_id=${draft.id}`
    : `/customer/requests/new-requests/view/${draft.workflow_version_id}?draft_id=${draft.id}&request_name=${encodeURIComponent(studyName || '')}`;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return convertUTCToLocal(date, 'dd/MM/yy HH:mm');
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) {
      onDelete(draft.id);
    }
  };

  // Get last action date (drafted_at for drafts)
  const lastActionDate = draft?.draftedAt || draft?.drafted_at;

  return (
    <Paper className="trial-card">
      <div className="header-container">
        <div className="header-left-container">
          <span className="status-badge draft">{t('Draft')}</span>
        </div>
        <div className="header-right-container">
          <button
            onClick={handleDelete}
            className="delete-button"
            title={t('Delete Draft')}
          >
            <Trash />
          </button>
        </div>
      </div>

      <div className="info-row">
        <div className="info-label">
          <span className="icon-wrapper">
            <Doctor2 />
          </span>
          <Typography className="row-text-header" variant="bodySmall">
            {t('Request Name')}:
          </Typography>
        </div>
        <Typography className="row-text-data" variant="bodySmall">
          {studyName}
        </Typography>
      </div>

      <div className="info-row">
        <div className="info-label">
          <span className="icon-wrapper">
            <ChartPie />
          </span>
          <Typography className="row-text-header" variant="bodySmall">
            {t('Request ID')}:
          </Typography>
        </div>
        <Typography className="row-text-data" variant="bodySmall">
          {generatedId}
        </Typography>
      </div>

      <div className="info-row">
        <div className="info-label">
          <span className="icon-wrapper">
            <ArrowRight />
          </span>
          <Typography className="row-text-header" variant="bodySmall">
            {t('Stage Name')}:
          </Typography>
        </div>
        <Typography className="row-text-data" variant="bodySmall">
          {stageName}
        </Typography>
      </div>

      <div className="info-row">
        <div className="info-label">
          <span className="icon-wrapper">
            <Table />
          </span>
          <Typography className="row-text-header" variant="bodySmall">
            {t('Saving date')}:
          </Typography>
        </div>
        <Typography className="row-text-data" variant="bodySmall">
          {formatDate(draft.createdAt || draft.created_at)}
        </Typography>
      </div>

      <div className="info-row">
        <div className="info-label">
          <span className="icon-wrapper">
            <Route />
          </span>
          <Typography className="row-text-header" variant="bodySmall">
            {t('Last action date')}:
          </Typography>
        </div>
        <Typography className="row-text-data" variant="bodySmall">
          {formatDate(lastActionDate)}
        </Typography>
      </div>

      <div className="view-details">
        <Link
          to={navigateTo}
          state={requestId ? { isRequestActive: true } : undefined}
          className="link-style"
        >
          {t('View Details')}
        </Link>
      </div>
    </Paper>
  );
};

export default DraftCard;
